import { and, eq, type PowerSyncDrizzleDB, user_collection } from "@famidex/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";
import { getCollectionItemByProductQuery } from "./queries";

export type CollectionState = "owned" | "wishlist" | "duplicate";

export interface AddToCollectionOpts {
  state?: CollectionState;
  condition?: string | null;
  notes?: string | null;
}

export interface AddToCollectionResult {
  id: string;
  /** true si se insertó una fila nueva; false si se actualizó una existente. */
  inserted: boolean;
  /** true si la nueva/actualizada quedó en estado `owned` (dispara +50 XP). */
  ownedNow: boolean;
}

/**
 * 🎯 AÑADIR A COLECCIÓN — patrón UPDATE-or-INSERT sobre la VISTA local-first.
 *
 * Las vistas PowerSync NO admiten `INSERT … ON CONFLICT` (gotcha #15), así que
 * la unicidad por (user_id, product_id) la resuelve el CLIENTE: SELECT existente
 * → UPDATE (sube cantidad / cambia estado / des-borra) o INSERT. El servidor
 * (write-path `db/sync.rs`) reaplica el mismo id con el user_id estampado
 * (aislamiento). Añadir un producto en estado `owned` estampa +50 XP por el
 * ledger de gamificación (lo cablea Wave 2 sobre el `ownedNow` que devolvemos).
 */
export async function addToCollectionMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  productId: string,
  opts?: AddToCollectionOpts,
): Promise<AddToCollectionResult> {
  const now = nowISO();
  const state: CollectionState = opts?.state ?? "owned";
  const condition = opts?.condition ?? null;
  const notes = opts?.notes ?? null;

  const existing = await getCollectionItemByProductQuery(db, userId, productId);
  const current = existing[0];

  if (current) {
    // UPDATE: re-añadir sube cantidad y des-borra (deleted_at → null).
    const wasLive = current.deleted_at == null;
    const wasOwned = current.state === "owned" && wasLive;
    const currentQty = current.quantity ?? 1;
    // Re-añadir un ítem vivo en propiedad suma una unidad; des-borrar uno de la
    // papelera lo deja tal cual (la cantidad ya reflejaba lo poseído).
    const nextQuantity = state === "owned" && wasLive ? currentQty + 1 : currentQty;
    await db
      .update(user_collection)
      .set({
        state,
        quantity: nextQuantity,
        condition: condition ?? current.condition ?? null,
        notes: notes ?? current.notes ?? null,
        deleted_at: null,
        acquired_at: current.acquired_at ?? now,
        updated_at: now,
      })
      .where(and(eq(user_collection.user_id, userId), eq(user_collection.id, current.id)));
    return { id: current.id, inserted: false, ownedNow: state === "owned" && !wasOwned };
  }

  // INSERT: primera vez que el usuario registra este producto.
  const id = generateUUID();
  await db.insert(user_collection).values({
    id,
    user_id: userId,
    product_id: productId,
    state,
    quantity: 1,
    condition,
    notes,
    acquired_at: now,
    deleted_at: null,
    created_at: now,
    updated_at: now,
  });
  return { id, inserted: true, ownedNow: state === "owned" };
}

/** Actualiza campos de un ítem de colección (estado / cantidad / condición / notas). */
export async function updateCollectionItemMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  id: string,
  updates: Partial<{
    state: CollectionState;
    quantity: number;
    condition: string | null;
    notes: string | null;
  }>,
): Promise<void> {
  await db
    .update(user_collection)
    .set({ ...updates, updated_at: nowISO() })
    .where(and(eq(user_collection.user_id, userId), eq(user_collection.id, id)));
}

/** Envía un ítem a la papelera (soft-delete). El barrido definitivo es server-side. */
export async function removeFromCollectionMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  id: string,
): Promise<void> {
  const now = nowISO();
  await db
    .update(user_collection)
    .set({ deleted_at: now, updated_at: now })
    .where(and(eq(user_collection.user_id, userId), eq(user_collection.id, id)));
}
